import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getAllBrandPresets, getEmbedUrlForBrand } from '../config/brandPresets';
import { format } from 'date-fns';

const { FiEdit2, FiTrash2, FiExternalLink, FiCopy, FiMove } = FiIcons;

function EmbedCard({ embed, index, onEdit, onDelete, onMove, dragHandleProps = {} }) {
  const [brandPresets, setBrandPresets] = useState({});

  useEffect(() => {
    loadBrandPresets();
  }, []);

  const loadBrandPresets = async () => {
    const presets = await getAllBrandPresets();
    setBrandPresets(presets);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const getEmbedUrl = (slug, brandId) => {
    return getEmbedUrlForBrand(slug, brandId, brandPresets);
  };

  const brand = brandPresets[embed.brand] || brandPresets.wwsc || {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden hover:shadow-xl transition-all group"
    >
      <div
        className="h-32 relative"
        style={{ backgroundColor: brand.primaryColor || '#2563eb' }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
          <div className="text-center text-white">
            <div className="text-lg font-bold">{brand.name || 'Brand'}</div>
            <div className="text-sm opacity-90">{embed.title}</div>
          </div>
        </div>
        {/* Drag handle */}
        <div className="absolute top-2 left-2">
          <span
            {...dragHandleProps}
            className="flex items-center justify-center w-7 h-7 text-white cursor-grab active:cursor-grabbing hover:bg-black hover:bg-opacity-20 rounded transition-colors"
            title="Drag to reorder"
          >
            <SafeIcon icon={FiMove} className="text-lg drop-shadow-lg" />
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
          {embed.title}
        </h3>
        <p className="text-sm text-gray-500 mb-3 line-clamp-1">
          {embed.toolUrl}
        </p>
        <div className="flex items-center justify-between text-xs text-gray-400 mb-4">
          <span>/{embed.slug}</span>
          <span>{format(new Date(embed.createdAt), 'MMM d')}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => copyToClipboard(getEmbedUrl(embed.slug, embed.brand))}
            className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
          >
            <SafeIcon icon={FiCopy} className="text-xs" />
            Copy Link
          </button>
          <button
            onClick={() => onMove(embed)}
            className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
            title="Move to section"
          >
            <SafeIcon icon={FiMove} />
          </button>
          <button
            onClick={() => window.open(getEmbedUrl(embed.slug, embed.brand), '_blank')}
            className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
          >
            <SafeIcon icon={FiExternalLink} />
          </button>
          <button
            onClick={() => onEdit(embed)}
            className="p-2 text-gray-500 hover:text-green-600 transition-colors"
          >
            <SafeIcon icon={FiEdit2} />
          </button>
          <button
            onClick={() => onDelete(embed.id)}
            className="p-2 text-gray-500 hover:text-red-600 transition-colors"
          >
            <SafeIcon icon={FiTrash2} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default EmbedCard;
